import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Check, ChevronLeft, ChevronRight, Calendar, Bed, Home, DoorOpen, X, ZoomIn } from 'lucide-react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { format, addDays, differenceInDays, startOfToday } from 'date-fns';
import { toast } from 'sonner';
import { OFFER_NOTE, EXTRA_BED_NOTE } from '../lib/roomSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const normalizeUrl = (url) =>
  url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const RoomDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [guests, setGuests] = useState(1);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    fetchRoom();
  }, [slug]);

  useEffect(() => {
    if (room?.id) {
      fetchInventory();
    }
  }, [room?.id]);

  useEffect(() => {
    if (room?.id && checkIn && checkOut) {
      fetchLiveAvailability();
    }
  }, [room?.id, checkIn, checkOut]);

  const fetchRoom = async () => {
    try {
      const response = await axios.get(`${API}/rooms/${slug}`);
      setRoom(response.data);
    } catch (error) {
      console.error('Error fetching room:', error);
      toast.error('Room not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API}/rooms/${room.id}/inventory`);
      setInventory(response.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  // Merge our DB availability with the live channel-manager (AsiaTech) count.
  // Effective units = the smaller of the two, since a unit can be taken either
  // on our site (DB) or on an OTA (live). Live-unknown falls back to DB only.
  const fetchEffectiveAvailability = async () => {
    const ci = format(checkIn, 'yyyy-MM-dd');
    const co = format(checkOut, 'yyyy-MM-dd');
    const dbResp = await axios.get(
      `${API}/rooms/${room.id}/availability?check_in=${ci}&check_out=${co}`
    );
    const data = { ...dbResp.data };

    let liveUnits = null;
    try {
      const liveResp = await axios.get(`${API}/live-availability?check_in=${ci}&check_out=${co}`);
      const u = liveResp.data?.rooms?.[room.slug]?.available_units;
      if (typeof u === 'number') liveUnits = u;
    } catch (e) {
      // scrape down -> silently keep DB-only numbers (never block a booking)
    }

    data.live_units = liveUnits;
    if (liveUnits !== null) {
      data.available_units = Math.min(data.available_units, liveUnits);
      data.available = data.available_units > 0;
    }
    return data;
  };

  const fetchLiveAvailability = async () => {
    try {
      setAvailability(await fetchEffectiveAvailability());
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const checkAvailability = async () => {
    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    setChecking(true);
    try {
      const data = await fetchEffectiveAvailability();
      setAvailability(data);

      if (data.available) {
        toast.success(`${data.available_units} of ${data.total_units} units available! Proceeding to booking...`);
        navigate(`/book?room=${room.id}&checkIn=${format(checkIn, 'yyyy-MM-dd')}&checkOut=${format(checkOut, 'yyyy-MM-dd')}&guests=${guests}`);
      } else {
        toast.error('All units are booked for selected dates');
      }
    } catch (error) {
      toast.error('Error checking availability');
    } finally {
      setChecking(false);
    }
  };

  const nextImage = () => {
    if (room) {
      setCurrentImageIndex((prev) => (prev + 1) % room.images.length);
    }
  };

  const prevImage = () => {
    if (room) {
      setCurrentImageIndex((prev) => (prev - 1 + room.images.length) % room.images.length);
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (!lightboxOpen) return;
    if (e.key === 'Escape') setLightboxOpen(false);
    if (e.key === 'ArrowRight') setCurrentImageIndex((prev) => (prev + 1) % room.images.length);
    if (e.key === 'ArrowLeft') setCurrentImageIndex((prev) => (prev - 1 + room.images.length) % room.images.length);
  }, [lightboxOpen, room]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;
  const totalPrice = room ? nights * room.price_per_night : 0;

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center">
        <h1 className="text-2xl font-heading mb-4">Room Not Found</h1>
        <Link to="/stay" className="btn-outline">Back to Rooms</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>{room ? `${room.name} | Rudragarh Resort` : 'Room Details | Rudragarh Resort'}</title>
        <meta name="description" content={room ? `${room.name} at Rudragarh Resort near Jaisalmer — sleeps ${room.max_guests}, from ₹${room.price_per_night?.toLocaleString('en-IN')}/night. ${room.description ? room.description.substring(0, 100) : ''}` : 'View room details at Rudragarh Resort near Jaisalmer, Rajasthan.'} />
        <meta property="og:title" content={room ? `${room.name} | Rudragarh Resort` : 'Room Details | Rudragarh Resort'} />
        <meta property="og:description" content={room ? `Stay in the ${room.name} at Rudragarh — a boutique desert resort near Jaisalmer, Rajasthan.` : 'Boutique desert rooms near Jaisalmer at Rudragarh Resort.'} />
        {room && <meta property="og:url" content={`https://www.rudragarh.com/stay/${room.slug}`} />}
        <meta property="og:image" content={room && room.images && room.images.length > 0 ? normalizeUrl(room.images[0]) : 'https://www.rudragarh.com/images/resort-pool-night.jpg'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={room ? `${room.name} | Rudragarh Resort` : 'Room Details | Rudragarh Resort'} />
        <meta name="twitter:description" content={room ? `Stay in the ${room.name} at Rudragarh — a boutique desert resort near Jaisalmer, Rajasthan.` : 'Boutique desert rooms near Jaisalmer at Rudragarh Resort.'} />
        <meta name="twitter:image" content={room && room.images && room.images.length > 0 ? normalizeUrl(room.images[0]) : 'https://www.rudragarh.com/images/resort-pool-night.jpg'} />
        {room && <link rel="canonical" href={`https://www.rudragarh.com/stay/${room.slug}`} />}
        {room && <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HotelRoom",
          "name": room.name,
          "description": room.description || room.short_description,
          "url": `https://www.rudragarh.com/stay/${room.slug}`,
          "occupancy": {
            "@type": "QuantitativeValue",
            "maxValue": room.max_guests
          },
          "offers": {
            "@type": "Offer",
            "price": room.price_per_night,
            "priceCurrency": "INR",
            "availability": "https://schema.org/InStock"
          }
        })}</script>}
        {room && <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rudragarh.com"},
            {"@type": "ListItem", "position": 2, "name": "The Stay", "item": "https://www.rudragarh.com/stay"},
            {"@type": "ListItem", "position": 3, "name": room.name}
          ]
        })}</script>}
      </Helmet>
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto container-padding py-4">
        <div className="flex items-center gap-2 text-sm text-foreground/60">
          <Link to="/stay" className="hover:text-gold-500">The Stay</Link>
          <span>/</span>
          <span className="text-gold-500">{room.name}</span>
        </div>
      </div>

      {/* Image Gallery */}
      <section className="relative" data-testid="room-gallery">
        <div className="relative h-[60vh] overflow-hidden">
          {room.images && room.images.length > 0 ? (
            <button
              className="absolute inset-0 w-full h-full cursor-zoom-in group"
              onClick={() => setLightboxOpen(true)}
              aria-label="Open image gallery"
            >
              <motion.img
                key={currentImageIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                src={normalizeUrl(room.images[currentImageIndex])}
                alt={room.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 backdrop-blur-sm p-3 rounded-full">
                  <ZoomIn size={24} className="text-white" />
                </div>
              </div>
            </button>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-void-subtle to-void flex flex-col items-center justify-center gap-4">
              <Bed size={56} className="text-gold-500/20" />
              <span className="text-foreground/20 text-sm uppercase tracking-widest">Photos Coming Soon</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-void/30 pointer-events-none" />
          
          {/* Navigation Arrows */}
          {room.images && room.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-void/50 backdrop-blur-sm text-foreground hover:bg-gold-500 hover:text-black transition-all"
                data-testid="gallery-prev"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-void/50 backdrop-blur-sm text-foreground hover:bg-gold-500 hover:text-black transition-all"
                data-testid="gallery-next"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Dot Indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {room.images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentImageIndex ? 'bg-gold-500 w-8' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
        
        {/* Image Thumbnails Strip */}
        {room.images && room.images.length > 1 && (
          <div className="max-w-7xl mx-auto container-padding py-4">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {room.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 relative transition-all ${
                    index === currentImageIndex 
                      ? 'ring-2 ring-gold-500 ring-offset-2 ring-offset-void' 
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={normalizeUrl(image)}
                    alt={`${room.name} - View ${index + 1}`}
                    className="w-24 h-16 md:w-32 md:h-20 object-cover"
                  />
                  {index === currentImageIndex && (
                    <div className="absolute inset-0 border-2 border-gold-500"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Room Details */}
      <section className="section-padding" data-testid="room-details">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left Content */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <span className="overline">{room.category}</span>
                <h1 className="text-4xl md:text-5xl font-heading font-medium mb-4">
                  {room.name}
                </h1>
                
                {/* Inventory Info */}
                {inventory && (
                  <div className="flex flex-wrap items-center gap-4 mb-4 p-4 bg-gold-500/10 border border-gold-500/20">
                    <div className="flex items-center gap-2">
                      <Home size={18} className="text-gold-500" />
                      <span className="text-foreground/80">
                        <span className="text-gold-400 font-heading text-xl">{inventory.total_units}</span>
                        {' '}{inventory.total_units === 1 ? 'Unit' : 'Units'}
                      </span>
                    </div>
                    <span className="text-foreground/20">•</span>
                    <div className="flex items-center gap-2">
                      <Bed size={18} className="text-gold-500" />
                      <span className="text-foreground/80">
                        <span className="text-gold-400 font-heading text-xl">{inventory.total_beds}</span>
                        {' '}{inventory.beds_description || 'Beds'}
                      </span>
                    </div>
                    <span className="text-foreground/20">•</span>
                    <div className="flex items-center gap-2">
                      <Users size={18} className="text-gold-500" />
                      <span className="text-foreground/80">
                        Sleeps <span className="text-gold-400 font-heading text-xl">{inventory.total_capacity}</span>
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Live Availability */}
                {availability && checkIn && checkOut && (
                  <div className={`flex items-center gap-2 p-3 ${
                    availability.available 
                      ? 'bg-green-500/10 border border-green-500/20 text-green-400' 
                      : 'bg-red-500/10 border border-red-500/20 text-red-400'
                  }`}>
                    <DoorOpen size={18} />
                    <span className="font-medium">
                      {availability.available
                        ? `${availability.available_units} of ${availability.total_units} units available for selected dates`
                        : `All ${availability.total_units} units booked for selected dates`
                      }
                      {availability.live_units !== null && availability.live_units !== undefined && (
                        <span className="ml-2 text-xs opacity-70">· live from all channels</span>
                      )}
                    </span>
                  </div>
                )}
                
                <div className="flex items-center gap-4 text-foreground/60 mt-4">
                  <div className="flex items-center gap-2">
                    <Users size={18} />
                    <span>Up to {room.max_guests} guests per unit</span>
                  </div>
                </div>
              </div>

              <p className="text-foreground/70 leading-relaxed text-lg">
                {room.description}
              </p>

              {/* Features */}
              <div>
                <h3 className="font-heading text-xl mb-4 text-gold-500">Features</h3>
                <div className="grid grid-cols-2 gap-4">
                  {room.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Check size={18} className="text-gold-500" />
                      <span className="text-foreground/80">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Amenities */}
              <div>
                <h3 className="font-heading text-xl mb-4 text-gold-500">Amenities</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {room.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-void-subtle">
                      <Check size={16} className="text-gold-500 flex-shrink-0" />
                      <span className="text-foreground/70 text-sm">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Extra Info (e.g. extra bedding note, cave bathroom note) */}
              {room.extra_info && (
                <div className="p-4 border border-gold-500/20 bg-gold-500/5">
                  <p className="text-foreground/60 text-sm leading-relaxed">{room.extra_info}</p>
                </div>
              )}

              {/* Resort Facilities Included */}
              <div>
                <h3 className="font-heading text-xl mb-4 text-gold-500">Resort Facilities — All Included</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    'Swimming Pool', 'Fully Equipped Gym', 'Grand Dining Hall',
                    'Traditional Low-Sitting Hall', 'Live / Open Kitchen', 'Bar Room',
                    'Private Sand Dunes', 'Natural Pond & Waterfall', 'Horse Riding',
                    'ATV on the Dunes', 'Football, Archery & Air Gun', 'Rain Dance Floor',
                    'Kids\' Play Zone', 'Cycling, Jogging & Trekking Tracks',
                    'Cultural Programs', 'Open-Air Theatre', 'Greenhouse',
                    'Photo Booths across Property',
                  ].map((facility, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Check size={14} className="text-gold-500/70 flex-shrink-0" />
                      <span className="text-foreground/60 text-sm">{facility}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Check-in / Check-out */}
              <div className="p-4 bg-void-subtle border border-gold-500/10">
                <h3 className="font-heading text-lg mb-3 text-gold-500">Check-in & Check-out</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-foreground/40 uppercase tracking-wider text-xs mb-1">Check-in</p>
                    <p className="text-foreground/80">From 11:00 AM</p>
                  </div>
                  <div>
                    <p className="text-foreground/40 uppercase tracking-wider text-xs mb-1">Check-out</p>
                    <p className="text-foreground/80">By 10:00 AM</p>
                    <p className="text-foreground/40 text-xs mt-1">Flexible checkout available — let us know in advance.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Card */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 card-glass p-6 space-y-6" data-testid="booking-card">
                <div className="flex items-end gap-2 flex-wrap">
                  {room.original_price > room.price_per_night && (
                    <span className="text-foreground/40 line-through text-xl pb-0.5">
                      ₹{room.original_price.toLocaleString()}
                    </span>
                  )}
                  <span className="text-gold-500 font-heading text-3xl">
                    ₹{room.price_per_night.toLocaleString()}
                  </span>
                  <span className="text-foreground/50 pb-1">/ night</span>
                  {room.original_price > room.price_per_night && (
                    <span className="text-[10px] uppercase tracking-wider bg-gold-500/20 text-gold-400 px-2 py-0.5 rounded mb-1">
                      {Math.round((1 - room.price_per_night / room.original_price) * 100)}% off
                    </span>
                  )}
                </div>
                {room.original_price > room.price_per_night && (
                  <p className="text-xs text-gold-400 !mt-2">{OFFER_NOTE}</p>
                )}
                <div className="text-xs text-foreground/60 space-y-1 !mt-3">
                  <p className="flex items-center gap-1.5"><Check size={12} className="text-gold-500" /> Complimentary breakfast included</p>
                  <p className="flex items-center gap-1.5"><Bed size={12} className="text-gold-500" /> {EXTRA_BED_NOTE}</p>
                </div>

                {/* Date Selection */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-foreground/60 mb-2 block">Check-in</label>
                    <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal bg-white/5 border-white/10 hover:bg-white/10"
                          data-testid="checkin-trigger"
                        >
                          <Calendar className="mr-2 h-4 w-4 text-gold-500" />
                          {checkIn ? format(checkIn, 'PPP') : <span className="text-foreground/40">Select date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-void-paper border-gold-500/20" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={checkIn}
                          defaultMonth={checkIn || new Date()}
                          onSelect={(date) => {
                            setCheckIn(date);
                            if (date && checkOut && checkOut <= date) setCheckOut(null);
                            setCheckInOpen(false);
                            // flow straight into picking check-out
                            if (date) setTimeout(() => setCheckOutOpen(true), 150);
                          }}
                          disabled={(date) => date < startOfToday()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <label className="text-sm text-foreground/60 mb-2 block">Check-out</label>
                    <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal bg-white/5 border-white/10 hover:bg-white/10"
                          data-testid="checkout-trigger"
                        >
                          <Calendar className="mr-2 h-4 w-4 text-gold-500" />
                          {checkOut ? format(checkOut, 'PPP') : <span className="text-foreground/40">Select date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-void-paper border-gold-500/20" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={checkOut}
                          defaultMonth={checkOut || checkIn || new Date()}
                          onSelect={(date) => { setCheckOut(date); setCheckOutOpen(false); }}
                          disabled={(date) => date <= (checkIn || new Date())}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <label className="text-sm text-foreground/60 mb-2 block">Guests</label>
                    <select
                      value={guests}
                      onChange={(e) => setGuests(Number(e.target.value))}
                      className="w-full input-default px-4"
                      data-testid="guests-select"
                    >
                      {Array.from({ length: room.max_guests }, (_, i) => i + 1).map((num) => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? 'Guest' : 'Guests'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Price Summary */}
                {nights > 0 && (
                  <div className="border-t border-gold-500/10 pt-4 space-y-2">
                    <div className="flex justify-between text-foreground/70">
                      <span>₹{room.price_per_night.toLocaleString()} × {nights} nights</span>
                      <span>₹{totalPrice.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-heading text-lg">
                      <span>Total</span>
                      <span className="text-gold-500">₹{totalPrice.toLocaleString()}</span>
                    </div>
                  </div>
                )}

                <button
                  onClick={checkAvailability}
                  disabled={checking}
                  className="btn-primary w-full disabled:opacity-50"
                  data-testid="check-availability-btn"
                >
                  {checking ? 'Checking...' : 'Check Availability'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && room.images && room.images.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            {/* Close */}
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 p-2 text-white/70 hover:text-white hover:bg-white/10 transition-colors rounded-full z-10"
              aria-label="Close"
            >
              <X size={28} />
            </button>

            {/* Counter */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-sm tracking-widest z-10">
              {currentImageIndex + 1} / {room.images.length}
            </div>

            {/* Image */}
            <motion.img
              key={currentImageIndex}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              src={normalizeUrl(room.images[currentImageIndex])}
              alt={`${room.name} - ${currentImageIndex + 1}`}
              className="max-h-[90vh] max-w-[90vw] object-contain select-none"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Prev / Next */}
            {room.images.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((prev) => (prev - 1 + room.images.length) % room.images.length); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white hover:bg-white/10 transition-colors rounded-full"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={32} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((prev) => (prev + 1) % room.images.length); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white hover:bg-white/10 transition-colors rounded-full"
                  aria-label="Next image"
                >
                  <ChevronRight size={32} />
                </button>
              </>
            )}

            {/* Thumbnail strip */}
            {room.images.length > 1 && (
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto pb-1"
                onClick={(e) => e.stopPropagation()}
              >
                {room.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImageIndex(i)}
                    className={`flex-shrink-0 transition-all ${i === currentImageIndex ? 'ring-2 ring-gold-500' : 'opacity-50 hover:opacity-100'}`}
                  >
                    <img src={normalizeUrl(img)} alt="" className="w-16 h-10 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RoomDetail;
